import { api } from './client';
import type { Page } from './catalogs';

/** ч.1208: Звіт про внесок на підтримку працевлаштування осіб з інвалідністю
 *  (J0500701 юрособа / F0500701 ФОП), квартальний. values: {код поля XSD: значення}. */
export type DisabilityFormCode = 'J0500701' | 'F0500701';
export type DisabilityCategory = '091' | '092' | '093';

export interface DisabilityFields {
  /** Платник внеску: середньооблікова ≥ 8 і норматив не виконано. */
  is_payer?: boolean;
  category?: DisabilityCategory | null;
  norm_rate?: '2' | '4';
  martial_law_half?: boolean;
  norm_met?: boolean;
  below_threshold?: boolean;
  /** ЄДРПОУ ліквідованого платника (лише уточнюючий звіт правонаступника). */
  liquidated_tin?: string;
}

export interface DisabilityCalcMonth {
  month: number; calendar_days: number; working_days: number;
  skshp_avg: string; skshp: number; kser_avg: string; kser: number;
  disabled_avg: string; disabled: number; double_avg: string; double: number;
  external_hours: string; norm_hours: string; min_wage: string; has_payroll: boolean;
}
export interface DisabilityCalcEmployee {
  employee_id: string; name: string; kind: 'MAIN' | 'INTERNAL' | 'EXTERNAL';
  employment_type: 'STAFF' | 'CIVIL_LAW'; hazardous: boolean; disabled: boolean;
  double_count: boolean; base_days: Record<string, number>;
  excluded_days: Record<string, number>; norm_days: Record<string, number>;
  salary_ok: Record<string, boolean>; gross_quarter: string; paid_in_quarter: boolean;
  hours_quarter: string | null;
  /** ч.1209: дні військової служби / призупиненого договору (у чисельності, без зарплати). */
  no_wage_days?: Record<string, number>;
  absence_note?: string | null;
}
export interface DisabilityCalc {
  months?: DisabilityCalcMonth[];
  employees?: DisabilityCalcEmployee[];
  computed?: {
    R011G3: number; R012G3: number; R013G3: number; R014G3: number; R0141G3: number;
    R015G3: number; R021G3: string; R022G3: string; R023G3: string;
    kser: number; kser_staff: number; fte: string; p_months: number;
    external_hours: string; norm_hours: string; category: DisabilityCategory;
  };
  warnings?: string[];
}

export interface DisabilityContributionReport {
  id: string;
  organization_id: string;
  document_number: string;
  document_date: string;
  form_code: DisabilityFormCode;
  report_type: 'REPORTING' | 'REPORTING_NEW' | 'CORRECTING';
  period_year: number;
  period_quarter: number;
  corrected_period_year: number | null;
  corrected_period_quarter: number | null;
  doc_cnt: number;
  values: Record<string, string>;
  fields: DisabilityFields;
  calc: DisabilityCalc;
  comment: string | null;
  deletion_mark: boolean;
  created_at: string;
}

/** Заповнені поля бланка для друкованої форми — те саме, що йде у файл для ДПС (ч.1213). */
export interface DisabilityReportBlank {
  form_code: 'J0500701' | 'F0500701';
  report_type: string;
  period_year: number;
  period_quarter: number;
  is_payer: boolean;
  /** {код поля XSD: значення}; відсутній ключ = поле порожнє. */
  fields: Record<string, string>;
  /** Чого бракує в картці організації, щоб сформувати файл. */
  missing: string[];
}

export interface DisabilityReportCheck {
  errors: { form: string; message: string }[];
  warnings: { form: string; message: string }[];
  form_code: string;
}

export const DISABILITY_FORM_TITLE =
  'Звіт про нарахування, обчислення та сплату внеску на підтримку працевлаштування осіб з інвалідністю';
export const QUARTER_LABELS: Record<number, string> = {
  1: 'I квартал', 2: 'II квартал', 3: 'III квартал', 4: 'IV квартал',
};
export const REPORT_TYPE_LABELS: Record<string, string> = {
  REPORTING: 'Звітний', REPORTING_NEW: 'Звітний новий', CORRECTING: 'Уточнюючий',
};
export const CATEGORY_LABELS: Record<DisabilityCategory, string> = {
  '091': 'Платник внеску, у якого середньооблікова кількість штатних працівників облікового складу за квартал — від 8 до 25 працівників',
  '092': 'Платник внеску, у якого середньооблікова кількість штатних працівників облікового складу за квартал — більше 25 працівників',
  '093': 'Підприємство, установа, організація, основним видом діяльності якої є реабілітація осіб з інвалідністю, навчання таких осіб або догляд за ними',
};

const BASE = '/disability-contribution-reports';

export const disabilityContributionReports = {
  list: (params?: {
    organization_id?: string; period_year?: number; period_quarter?: number;
    sort_by?: string; sort_dir?: 'asc' | 'desc'; limit?: number; offset?: number;
  }) => api.get<Page<DisabilityContributionReport>>(BASE, { params }).then((r) => r.data),
  get: (id: string) =>
    api.get<DisabilityContributionReport>(`${BASE}/${id}`).then((r) => r.data),
  create: (p: {
    organization_id: string; period_year: number; period_quarter: number;
    report_type?: string; comment?: string | null;
    corrected_period_year?: number; corrected_period_quarter?: number;
  }) => api.post<DisabilityContributionReport>(BASE, p).then((r) => r.data),
  update: (id: string, p: {
    report_type?: string; comment?: string | null; document_date?: string;
    values?: Record<string, string>; fields?: DisabilityFields;
    /** Номер Звіту за звітний період (1…999) — у файлі HNUM / C_DOC_CNT. */
    doc_cnt?: number;
  }) => api.patch<DisabilityContributionReport>(`${BASE}/${id}`, p).then((r) => r.data),
  recalc: (id: string) =>
    api.post<DisabilityContributionReport>(`${BASE}/${id}/recalc`).then((r) => r.data),
  check: (id: string) =>
    api.get<DisabilityReportCheck>(`${BASE}/${id}/check`).then((r) => r.data),
  blank: (id: string) =>
    api.get<DisabilityReportBlank>(`${BASE}/${id}/blank`).then((r) => r.data),
  remove: (id: string) => api.delete(`${BASE}/${id}`).then(() => undefined),
  xml: (id: string) => api.get(`${BASE}/${id}/xml`, { responseType: 'blob' }),
  files: (id: string) =>
    api.get<{ files: { fname: string; xml_base64: string }[] }>(`${BASE}/${id}/files`)
      .then((r) => r.data),
  submission: (id: string) =>
    api.get<{ submission_id: string | null; status: string | null;
      receipt_accepted?: boolean | null }>(`${BASE}/${id}/submission`).then((r) => r.data),
};

/** Клієнтські дзеркала формул бланка (сервер — джерело істини у «перевірити»). */
export function normWorkplaces(skshp: number, twoPct: boolean): number {
  if (skshp < 8) return 0;
  const half = (v: number) => Math.floor(v + 0.5);
  if (twoPct) return half(skshp * 0.02);
  if (skshp <= 25) return 1;
  return half(skshp * 0.04);
}
export function shortfall(norm: number, disabled: number, double: number): number {
  return Math.max(0, norm - (disabled - double) - double * 2);
}
/** S = 40 % × ЗПсер × 3 × ряд.5; на час воєнного стану × 50 % (у копійках, без похибки float). */
export function contributionAmount(avgWage: string | number, missing: number, martialLaw: boolean): string {
  const kop = Math.round(Number(avgWage || 0) * 100);
  // 40 % × 3 = 1.2; × 50 % = 0.6 → у цілих: kop × missing × 12 / 10 (або × 6 / 10).
  const num = kop * missing * (martialLaw ? 6 : 12);
  const res = Math.floor((num + 5) / 10);
  return (res / 100).toFixed(2);
}
